import { useMemo, useState, Fragment } from 'react';
import { DropdownTypeEnum, FilterDropdown } from '@ui';
import {
  AirportStartLocationType,
  CustomStartLocationType,
  StartLocationSettingsType,
  SuggestedSettingsEnum,
} from '@lp/features/RoutePlanner/types';
import { useRouteLocations } from '@lp/features/RoutePlanner/hooks/useRouteLocations';
import { RouteTypeSettingsEnum, StartLocationObjectType } from '@/api/apis/mission-planner';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';
import { useLogisticsList } from '@/queries/route-planner';
import { SuggestedSelectTrigger } from './SuggestedSelectTrigger';
import { StartLocationAirportDropdown } from './StartLocationAirportDropdown';
import { StartLocationCustomSelect } from './StartLocationCustomSelect';
import {
  getLocationCountryCodes,
  getLocationListItem,
  getRestrictedStartLocationIds,
  getStartLocationString,
  ON_REFERENCE_CASE_SECTION,
  StartLocationListItemType,
} from './utils';

const CUSTOM_LOCATION_NAME_MAP: Partial<Record<StartLocationObjectType, string>> = {
  [StartLocationObjectType.Airport]: 'Airport',
  [StartLocationObjectType.Custom]: 'Custom',
};

const CUSTOM_LOCATIONS: StartLocationListItemType[] = [
  {
    value: StartLocationObjectType.Airport,
    section: 'Other',
    name: CUSTOM_LOCATION_NAME_MAP[StartLocationObjectType.Airport] || '',
    data: null,
  },
  {
    value: StartLocationObjectType.Custom,
    section: 'Other',
    name: CUSTOM_LOCATION_NAME_MAP[StartLocationObjectType.Custom] || '',
    data: null,
  },
];

type StartLocationSettingsProps = {
  routeType: RouteTypeSettingsEnum;
  data: StartLocationSettingsType;
  handleSettingsChange: (data: StartLocationSettingsType) => void;
};

export const StartLocationSettings = ({ routeType, data, handleSettingsChange }: StartLocationSettingsProps) => {
  const { reAllocationCaseId, donorHospitalDetails, txCenterDetails } = useBaseCaseDetails();
  const { data: logisticsList } = useLogisticsList(reAllocationCaseId ? { caseIds: reAllocationCaseId } : {});
  const { list: locations } = useRouteLocations();

  const { locationsList, existingLocationIds, countryCodes } = useMemo(() => {
    const restrictedIds = getRestrictedStartLocationIds(routeType, donorHospitalDetails?.id, txCenterDetails?.id);

    const prevCaseLocations = (logisticsList?.items || []).flatMap((l) =>
      l.locations.map((location) => getLocationListItem(location, ON_REFERENCE_CASE_SECTION)),
    );
    const currentCaseLocations = (locations || []).map((l) => getLocationListItem(l));
    const allLocations = [...prevCaseLocations, ...currentCaseLocations];

    const existingLocations = Array.from(
      new Map(allLocations.map((location) => [location.value, location])).values(),
    ).filter((l) => !restrictedIds.has(l.value));

    return {
      locationsList: [...existingLocations, ...CUSTOM_LOCATIONS],
      existingLocationIds: new Set(existingLocations.map((l) => l.value)),
      countryCodes: getLocationCountryCodes(allLocations),
    };
  }, [logisticsList, locations, routeType, donorHospitalDetails, txCenterDetails]);

  const isExistingLocation = !!data.value?.id && existingLocationIds.has(data.value.id);

  const valueCustomType =
    data.value && !isExistingLocation ? (CUSTOM_LOCATION_NAME_MAP[data.value.type] && data.value.type) || null : null;

  const [pickedCustomType, setPickedCustomType] = useState<StartLocationObjectType | null>(valueCustomType);

  const selectedLocationType = data.value ? valueCustomType : pickedCustomType;

  const handleLocationSelect = (keys: string[]) => {
    const item = locationsList.find((location) => location.value === keys[0]);

    if (!item) return;

    handleSettingsChange({
      type: SuggestedSettingsEnum.StartLocation,
      value: item ? item.data : null,
    });

    setPickedCustomType(item.data ? null : (item.value as StartLocationObjectType));
  };

  const handleCustomLocationSelect = (
    customLocationData: AirportStartLocationType | CustomStartLocationType | null,
  ) => {
    handleSettingsChange({
      type: SuggestedSettingsEnum.StartLocation,
      value: customLocationData,
    });
  };

  const selectedCustomValue = selectedLocationType ? [selectedLocationType] : [];
  const selectedStartLocationValue = data.value?.id ? [data.value.id] : selectedCustomValue;

  return (
    <Fragment key={data.type}>
      <FilterDropdown
        placement="top-start"
        title="Start Location"
        items={locationsList}
        type={DropdownTypeEnum.Sections}
        disallowEmptySelection
        renderCustomTrigger={(isOpen) => (
          <SuggestedSelectTrigger
            title="Start Location:"
            value={
              selectedLocationType
                ? CUSTOM_LOCATION_NAME_MAP[selectedLocationType] || ''
                : getStartLocationString(data.value)
            }
            isOpen={isOpen}
          />
        )}
        selectedKeys={selectedStartLocationValue}
        mode="single"
        onSelectedKeysChange={handleLocationSelect}
      />
      {selectedLocationType === StartLocationObjectType.Airport && (
        <StartLocationAirportDropdown
          data={data.value as AirportStartLocationType | null}
          handleAirportSelect={handleCustomLocationSelect}
        />
      )}
      {selectedLocationType === StartLocationObjectType.Custom && (
        <StartLocationCustomSelect
          data={data.value as CustomStartLocationType | null}
          countryCodes={countryCodes}
          handleCustomAddressSelect={handleCustomLocationSelect}
        />
      )}
    </Fragment>
  );
};
